'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterSidebarProps {
  onFilterChange: (filters: FilterState) => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

export interface FilterState {
  categories: string[];
  levels: string[];
  duration: string[];
  priceRange: string[];
}

export default function FilterSidebar({ onFilterChange, isMobileOpen, onMobileClose }: FilterSidebarProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    categories: [],
    levels: [],
    duration: [],
    priceRange: [],
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const filterOptions = {
    categories: [
      'Web Development',
      'Data Science',
      'Mobile Development',
      'Cloud Computing',
      'Cybersecurity',
      'AI & Machine Learning',
    ],
    levels: ['Beginner', 'Intermediate', 'Advanced'],
    duration: ['0-3 months', '3-6 months', '6-12 months', '12+ months'],
    priceRange: ['Free', '₹0-₹41,500', '₹41,500-₹83,000', '₹83,000+'],
  };

  const handleFilterToggle = (category: keyof FilterState, value: string) => {
    if (!isHydrated) return;

    const newFilters = { ...filters };
    const index = newFilters[category].indexOf(value);

    if (index > -1) {
      newFilters[category].splice(index, 1);
    } else {
      newFilters[category].push(value);
    }

    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearAllFilters = () => {
    if (!isHydrated) return;

    const clearedFilters = {
      categories: [],
      levels: [],
      duration: [],
      priceRange: [],
    };
    setFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const activeFilterCount = Object.values(filters).flat().length;

  const FilterSection = ({ title, options, category }: { title: string; options: string[]; category: keyof FilterState }) => (
    <div className="mb-6">
      <h3 className="text-sm font-headline font-semibold text-foreground mb-3">{title}</h3>
      <div className="space-y-2">
        {options.map((option) => (
          <label key={option} className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={isHydrated ? filters[category].includes(option) : false}
              onChange={() => handleFilterToggle(category, option)}
              className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer"
            />
            <span className="text-sm font-body text-muted-foreground group-hover:text-foreground transition-colors duration-200">
              {option}
            </span>
          </label>
        ))}
      </div>
    </div>
  );

  const sidebarContent = (
    <>
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="FunnelIcon" size={20} variant="outline" className="text-primary" />
          <h2 className="text-lg font-headline font-bold text-foreground">Filters</h2>
          {isHydrated && activeFilterCount > 0 && (
            <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-headline font-semibold rounded-full">
              {activeFilterCount}
            </span>
          )}
        </div>
        {isMobileOpen && (
          <button
            onClick={onMobileClose}
            className="lg:hidden p-1 hover:bg-muted rounded-md transition-colors duration-200"
            aria-label="Close filters"
          >
            <Icon name="XMarkIcon" size={20} variant="outline" />
          </button>
        )}
      </div>

      {isHydrated && activeFilterCount > 0 && (
        <button
          onClick={clearAllFilters}
          className="w-full mb-6 py-2 px-4 bg-muted text-foreground rounded-lg font-body font-medium text-sm hover:bg-muted/80 transition-colors duration-200"
        >
          Clear All Filters
        </button>
      )}

      <FilterSection title="Category" options={filterOptions.categories} category="categories" />
      <FilterSection title="Skill Level" options={filterOptions.levels} category="levels" />
      <FilterSection title="Duration" options={filterOptions.duration} category="duration" />
      <FilterSection title="Price Range" options={filterOptions.priceRange} category="priceRange" />
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-72 bg-card rounded-xl shadow-sky-sm p-6 h-fit sticky top-20">
        {sidebarContent}
      </aside>

      {/* Mobile Sidebar */}
      {isMobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-foreground/50" onClick={onMobileClose}>
          <aside
            className="absolute left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-card shadow-sky-lg p-6 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
}